/**
 * Postcode Icon Components
 * 
 * Coordal and GhanaPost logo icons for displaying postcodes
 * Premium styling for product-grade UI
 */

import { Copy, Plus } from 'lucide-react';

interface IconProps {
  size?: number;
  className?: string;
}

/**
 * Coordal Logo Icon - Brand globe/location design without background
 */
export function CoordalIcon({ size = 16, className = '' }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 40 40"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      <defs>
        <linearGradient id="coordalIconGrad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" style={{ stopColor: '#06b6d4', stopOpacity: 1 }} />
          <stop offset="100%" style={{ stopColor: '#3b82f6', stopOpacity: 1 }} />
        </linearGradient>
      </defs>
      <ellipse cx="20" cy="20" rx="8" ry="17" stroke="url(#coordalIconGrad)" strokeWidth="1.5" fill="none" opacity="0.7" />
      <ellipse cx="20" cy="20" rx="17" ry="8" stroke="url(#coordalIconGrad)" strokeWidth="1.5" fill="none" opacity="0.7" />
      <line x1="3" y1="20" x2="37" y2="20" stroke="#06b6d4" strokeWidth="2" opacity="0.35" />
      <line x1="20" y1="3" x2="20" y2="37" stroke="#06b6d4" strokeWidth="2" opacity="0.35" />
      <circle cx="26" cy="14" r="5" fill="url(#coordalIconGrad)" />
      <circle cx="26" cy="14" r="2" fill="white" />
    </svg>
  );
}

/**
 * GhanaPost GPS Logo Icon - Orange/blue GPS style
 * Based on official GhanaPost GPS branding
 */
export function GhanaPostIcon({ size = 16, className = '' }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 32 32"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      {/* Orange background with rounded corners */}
      <rect x="2" y="2" width="28" height="28" rx="6" fill="#f97316" />
      {/* GPS pin icon */}
      <path
        d="M16 7C12.13 7 9 10.13 9 14C9 19.25 16 25 16 25C16 25 23 19.25 23 14C23 10.13 19.87 7 16 7ZM16 16.5C14.62 16.5 13.5 15.38 13.5 14C13.5 12.62 14.62 11.5 16 11.5C17.38 11.5 18.5 12.62 18.5 14C18.5 15.38 17.38 16.5 16 16.5Z"
        fill="white"
      />
      {/* Small GPS text indicator */}
      <rect x="19" y="19" width="8" height="6" rx="1" fill="#1d4ed8" />
      <text
        x="23"
        y="24"
        textAnchor="middle"
        fill="white"
        fontSize="5"
        fontWeight="bold"
        fontFamily="system-ui, sans-serif"
      >
        GPS
      </text>
    </svg>
  );
}

interface PostcodeDisplayProps {
  coordalCode: string;
  coordalDisplay?: string;
  ghanaPostCode?: string | null;
  onCopy?: (code: string, type: 'coordal' | 'ghanapost') => void;
  onAddGhanaPost?: () => void;
  compact?: boolean;
}

/**
 * PostcodeDisplay - Shows Coordal and GhanaPost codes side by side
 */
export function PostcodeDisplay({
  coordalCode,
  coordalDisplay,
  ghanaPostCode,
  onCopy,
  onAddGhanaPost,
  compact = false
}: PostcodeDisplayProps) {
  const displayCode = coordalDisplay || coordalCode;

  const handleCopy = (code: string, type: 'coordal' | 'ghanapost') => {
    navigator.clipboard.writeText(code);
    onCopy?.(code, type);
  };

  if (compact) {
    return (
      <div className="flex items-center gap-3 text-xs">
        {/* Coordal Postcode */}
        <button
          onClick={() => handleCopy(coordalCode, 'coordal')}
          className="flex items-center gap-1 text-gray-600 hover:text-emerald-600 transition-colors"
          title={`Copy Coordal: ${coordalCode}`}
        >
          <CoordalIcon size={14} />
          <span className="font-medium">{displayCode}</span>
        </button>

        {/* GhanaPost GPS */}
        {ghanaPostCode ? (
          <button
            onClick={() => handleCopy(ghanaPostCode, 'ghanapost')}
            className="flex items-center gap-1 text-gray-500 hover:text-blue-600 transition-colors"
            title={`Copy GhanaPost: ${ghanaPostCode}`}
          >
            <GhanaPostIcon size={14} />
            <span>{ghanaPostCode}</span>
          </button>
        ) : (
          <button
            onClick={onAddGhanaPost}
            className="flex items-center gap-1 text-gray-400 hover:text-blue-500 transition-colors"
            title="Add GhanaPost code"
          >
            <GhanaPostIcon size={14} className="opacity-50" />
            <span className="flex items-center gap-0.5">
              <Plus size={10} />
              <span>Add</span>
            </span>
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      {/* Coordal Postcode - Primary */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <CoordalIcon size={18} />
          <div>
            <p className="text-xs text-gray-500">Coordal Postcode</p>
            <p className="font-semibold text-gray-900">{displayCode}</p>
          </div>
        </div>
        <button
          onClick={() => handleCopy(coordalCode, 'coordal')}
          className="p-2 text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors"
          title="Copy Coordal postcode"
        >
          <Copy size={16} />
        </button>
      </div>

      {/* GhanaPost GPS - Secondary */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <GhanaPostIcon size={18} />
          <div>
            <p className="text-xs text-gray-500">GhanaPost GPS</p>
            {ghanaPostCode ? (
              <p className="font-medium text-gray-700">{ghanaPostCode}</p>
            ) : (
              <p className="text-gray-400 text-sm">Not available</p>
            )}
          </div>
        </div>
        {ghanaPostCode ? (
          <button
            onClick={() => handleCopy(ghanaPostCode, 'ghanapost')}
            className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
            title="Copy GhanaPost code"
          >
            <Copy size={16} />
          </button>
        ) : (
          <button
            onClick={onAddGhanaPost}
            className="px-3 py-1.5 text-xs text-blue-600 hover:bg-blue-50 rounded-lg transition-colors flex items-center gap-1"
          >
            <Plus size={12} />
            Add code
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * PostcodeRow - Inline display for search results
 */
export function PostcodeRow({
  coordalCode,
  coordalDisplay,
  ghanaPostCode,
  onCopy
}: Omit<PostcodeDisplayProps, 'onAddGhanaPost' | 'compact'>) {
  const displayCode = coordalDisplay || coordalCode;

  return (
    <div className="flex items-center gap-3 mt-1">
      {/* Coordal - Primary */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          navigator.clipboard.writeText(coordalCode);
          onCopy?.(coordalCode, 'coordal');
        }}
        className="flex items-center gap-1 text-xs text-gray-600 hover:text-emerald-600 transition-colors"
        title={`Copy: ${coordalCode}`}
      >
        <CoordalIcon size={14} />
        <span className="font-medium">{displayCode}</span>
      </button>

      {/* GhanaPost - Secondary */}
      {ghanaPostCode ? (
        <button
          onClick={(e) => {
            e.stopPropagation();
            navigator.clipboard.writeText(ghanaPostCode);
            onCopy?.(ghanaPostCode, 'ghanapost');
          }}
          className="flex items-center gap-1 text-xs text-gray-500 hover:text-blue-600 transition-colors"
          title={`Copy: ${ghanaPostCode}`}
        >
          <GhanaPostIcon size={14} />
          <span>{ghanaPostCode}</span>
        </button>
      ) : (
        <span className="flex items-center gap-1 text-xs text-gray-400">
          <GhanaPostIcon size={14} className="opacity-50" />
          <span>No GPS code</span>
        </span>
      )}
    </div>
  );
}
